import {
  BodyText,
  Button,
  DropdownButton,
  DropdownMenu,
  DropdownMenuItem,
  Icon,
  IconSet,
} from '@postscript/components';
import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';
import { useLocalStorage } from 'react-use';

import { useProgressiveOnboarding } from 'providers/ProgressiveOnboarding/ProgressiveOnboardingProvider';

import { Flex, Stack } from 'components/layout';
import { ProgressPanel } from 'components/ProgressiveOnboarding/ProgressPanel';

import type { Guide } from 'providers/ProgressiveOnboarding/types';

import classes from 'components/ProgressiveOnboarding/ProgressTracker.module.css';

export const ProgressTracker = ({ guide }: { guide: Guide }) => {
  const { activeGuide, setActiveGuide } = useProgressiveOnboarding();
  const { title, icon, steps } = guide;

  const [isDoubleConfirmOpen, setIsDoubleConfirmOpen] = useState(false);
  const [isHidden, setIsHidden] = useLocalStorage(
    `progressive_onboarding_${guide.key}_hidden`,
    false,
  );

  const totalSteps = steps.length;
  const completedSteps = steps.filter(
    (step) => step.state === 'complete',
  ).length;
  const progressPercentage = (completedSteps / totalSteps) * 100;

  const onClickHide = () => {
    setIsDoubleConfirmOpen(true);
  };

  if (isHidden || !guide.steps.some((step) => step.state !== 'complete')) {
    return null;
  }

  if (isDoubleConfirmOpen) {
    return (
      <div className={`${classes.progressTracker} ${classes[guide.key]}`}>
        <div className={classes.header}>
          <div className={classes.title}>
            <BodyText size="small" bold>
              Hide Setup Guide?
            </BodyText>
          </div>
        </div>

        <div className={classes.body}>
          <Stack gap="var(--spacing-4)">
            <BodyText size="x-small">
              Once hidden, you will be unable to re-open the setup guide
            </BodyText>

            <Flex gap="var(--spacing-2)" justifyContent="flex-start">
              <Button size="small" onClick={() => setIsHidden(true)}>
                Hide Guide
              </Button>
              <Button
                variant="plain"
                size="small"
                onClick={() => setIsDoubleConfirmOpen(false)}
              >
                Never Mind
              </Button>
            </Flex>
          </Stack>
        </div>
      </div>
    );
  }

  return (
    <Dialog.Root
      open={activeGuide?.key === guide.key}
      onOpenChange={(open) => {
        setActiveGuide(open ? guide : undefined);
      }}
      modal={false}
    >
      <Dialog.Portal>
        <Dialog.Content className={classes.PopoverContent}>
          <ProgressPanel guide={guide} />
        </Dialog.Content>
      </Dialog.Portal>

      <Dialog.Trigger asChild>
        <div
          role="button"
          className={`${classes.progressTracker} ${classes[guide.key]}`}
          tabIndex={0}
        >
          <div className={classes.header}>
            <div className={classes.title}>
              <Icon component={IconSet[icon]} size={24} />
              <BodyText size="small" bold>
                {title}
              </BodyText>
            </div>

            <DropdownButton
              icon={IconSet.DotsHorizontal}
              variant="plain"
              size="large"
              id="progress-tracker-dropdown"
              onClick={(e) => e.stopPropagation()}
            >
              <DropdownMenu aria-labelledby="progress-tracker-dropdown">
                <DropdownMenuItem>
                  <Button onClick={onClickHide}>Hide Setup Guide</Button>
                </DropdownMenuItem>
              </DropdownMenu>
            </DropdownButton>
          </div>

          <div className={classes.progress}>
            <BodyText size="small">
              {completedSteps}/{totalSteps}
            </BodyText>

            <div className={classes.progressBar}>
              <div
                className={classes.indicator}
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
        </div>
      </Dialog.Trigger>
    </Dialog.Root>
  );
};
